import { useEffect, useMemo, useState } from "react"
import services from "../../../services/services";
import _ from "underscore";
import { decryptData, encryptData } from "../../../utils/encrypt";
import { dateConvert, formatDateObject } from "../../../utils/Helper";
import { DateObject } from "react-multi-date-picker";

export const useProceduralError = ({ projectChanged }) => {

    const [procedureData, setProcedureData] = useState({ x_axis: [], y_axis: [], api_data: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState([]);
    const [hoveredRange, setHoveredRange] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOpts, setFilterOpts] = useState({ dbname: [], procedure: [] });
    const [filterValues, setFilterValues] = useState({ dbname: [], procedure: [], date: {} });
    const [filterCount, setFilterCount] = useState(0);
    const [expand, setExpand] = useState(false);
    const [time, setTime] = useState({ from: null, to: null });
    const [intervalFlag, setIntervalFlag] = useState(false);
    const [defaultClicked, setDefaultClicked] = useState(true);

    useEffect(() => {
        handleDateRange(null, true);
    }, [])

    useEffect(() => {
        getData()
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            if (defaultClicked) {
                handleDateRange(null, true);
            } else {
                getData();
            }
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    const handleDateRange = (e, defaultFlag) => {
        if (e === null) {
            const now = new DateObject();
            const oneMinuteAgo = new DateObject().subtract(1, "hour");
            const range = [new DateObject(oneMinuteAgo), new DateObject(now)];
            setHoveredRange(range);
            setTime({ from: oneMinuteAgo?.toDate(), to: now?.toDate() });
            defaultFlag && handleApplyFilters(range) && setDefaultClicked(true);
        } else {
            setHoveredRange(e);
            setDefaultClicked(false);
        }
    }

    const handleApplyFilters = (dateRange) => {
        let dates;
        if (dateRange) {
            dates = {
                from_date: dateConvert(dateRange?.[0]?.toDate(), "iso-string")?.split(".")?.[0],
                to_date: dateConvert(dateRange?.[1]?.toDate(), "iso-string")?.split(".")?.[0]
            }
        } else {
            dates = {
                from_date: formatDateObject(hoveredRange?.[0], time?.from),
                to_date: formatDateObject(hoveredRange?.[1], time?.to)
            }
        }
        const serviceFilters = {
            dates: dates,
            dbname: filterValues?.dbname,
            procedure: filterValues?.procedure
        }
        sessionStorage.setItem("pefilters", encryptData(JSON.stringify(serviceFilters)));

        // set count of filters applied
        let count = 0;
        if ((hoveredRange === undefined || _.isEmpty(hoveredRange)) && (dateRange === undefined || _.isEmpty(dateRange))) {
            count = 0;
        } else {
            count++;
        }
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        setShowFilter(false);
        sessionStorage.getItem("inspectra_error_view") && getData();
        setIntervalFlag(!intervalFlag);
    }

    const handleDefaultFilters = () => {
        handleDateRange(null, false);
        setDefaultClicked(true);
        setFilterValues((prev) => ({ ...prev, dbname: [], procedure: [] }));
    }

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject) && selectedProject?.host) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("pefilters")) || "{}");
                setFilterValues((prev) => ({ ...prev, date: filters?.dates }));
                const payload = {
                    "from_datetime": filters?.dates?.from_date,
                    "to_datetime": filters?.dates?.to_date,
                    "dbname": _.isEmpty(filters?.dbname) ? undefined : filters?.dbname?.toString(),
                    "procedure_name": _.isEmpty(filters?.procedure) ? undefined : filters?.procedure?.toString(),
                    "project_name": selectedProject?.project_key
                }
                const hostArray = _.pluck(selectedProject?.host, "domain_host_key");
                const responses = await Promise.all(
                    hostArray.map(async (host) => {
                        try {
                            const response = await services.callInspectra("POST", '/inspectra/proclogs/v1', payload, host);
                            return response?.data?.[0];
                        } catch (err) {
                            console.error(`Error fetching data for host ${host}`, err);
                            return null;
                        }
                    })
                );

                const dataArray = responses.filter(Boolean).flat();
                if (dataArray) {
                    const data = dataArray;
                    setProcedureData((prev) => ({ ...prev, api_data: data }));
                    let procedure = _.uniq(_.pluck(data, "ProcedureName"));
                    let dbname = _.uniq(_.pluck(data, "dbname"));
                    if (_.isEmpty(filters?.procedure) && _.isEmpty(filters?.dbname)) {
                        setFilterOpts((prev) => ({ ...prev, procedure: procedure, dbname: dbname }))
                    }
                    let withSync = [];
                    let withoutSync = [];
                    procedure?.forEach((procedure, i) => {
                        dbname?.forEach((db) => {
                            const findData = _.filter(data, { ProcedureName: procedure, dbname: db });
                            const withSyncData = _.filter(findData, (item) => item?.sync_type);
                            const withoutSyncData = _.filter(findData, (item) => !item?.sync_type);
                            withSync.push({
                                x: i,
                                y: withSyncData?.length === 0 ? null : withSyncData?.length,
                                z: 1,
                            });
                            withoutSync.push({
                                x: i,
                                y: withoutSyncData?.length === 0 ? null : withoutSyncData?.length,
                                z: 1,
                            });
                        })
                    })
                    setProcedureData((prev) => ({ ...prev, x_axis: procedure, y_axis: [{ name: "Sync Error", data: withSync, color: "rgba(255, 165, 0, 0.6)" }, { name: "Transaction Error", data: withoutSync, color: "rgba(0, 140, 255, 0.6)" }] }));
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("error getting procedural data: ", error);
            setLoader(false);
        }
    }

    const options = {
        credits: { enabled: false },
        chart: {
            type: 'bubble',
            inverted: true,
            events: {
                render: function () {
                    const chart = this;
                    chart.xAxis[0].ticks &&
                        Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label && !tick.label.clickAttached && isNaN(tick.label.textStr)) {
                                tick.label.element.style.cursor = 'pointer';
                                tick.label.element.onclick = () => {
                                    handleShowDetailsPopup(tick.label.textStr);
                                };
                                tick.label.clickAttached = true;
                            }
                        });
                }
            }
        },
        title: { text: '' },
        xAxis: {
            categories: procedureData?.x_axis,
            min: 0,
            labels: {
                style: {
                    width: '100px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            },
            title: { text: '' },
            gridLineDashStyle: 'solid',
            gridLineWidth: 1
        },
        yAxis: {
            tickInterval: 1,
            title: { text: 'Error Count' }
        },
        tooltip: {
            formatter: function () {
                return `<b>${procedureData?.x_axis[this.point.x]}</b><br/>Errors: <b>${this.point.y}</b>`;
            }
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'top',
            floating: false,
            backgroundColor: 'white',
            shadow: false
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const name = procedureData?.x_axis[this.x];
                            handleShowDetailsPopup(name, this);
                        }
                    }
                },
                enableMouseTracking: true,
            }
        },
        series: procedureData?.y_axis
    };

    const handleShowDetailsPopup = (procedure, thisVal) => {
        const data = _.filter(procedureData?.api_data, { ProcedureName: procedure })?.sort((a, b) => { return new Date(b?.LogDate) - new Date(a?.LogDate) });
        let filteredData = [];
        if (thisVal) {
            if (thisVal?.series?.name === "Sync Error") {
                filteredData = _.filter(data, (item) => item?.sync_type);
            } else {
                filteredData = _.filter(data, (item) => !item?.sync_type)
            }
        } else {
            filteredData = data;
        }
        setPopupData(filteredData);
        setShowPopup(true);
    }

    return useMemo(() => ({
        options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters
    }), [options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters])
}